import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    Chip,
    Divider,
    Link,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import IconButton from '@mui/material/IconButton'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PersonIcon from '@mui/icons-material/Person'

type Props = {
    apiData: any;
};

const buildPostsWithCommentVersions = (apiData: any) => {
    const entities = apiData?.apiDataEntities ?? []
    const postsMap = new Map<string, any>()

    for (const entity of entities) {
        const posts = Array.isArray(entity.data)
            ? entity.data
            : entity.data?.postsComments ?? []

        for (const post of posts) {
            if (!postsMap.has(post.postId)) {
                postsMap.set(post.postId, {
                    ...post,
                    comments: [],
                })
            }

            const existingPost = postsMap.get(post.postId)

            if (!existingPost) {
                continue
            }

            const commentsMap = new Map<string, any>(
                existingPost.comments.map((comment: any) => [
                    comment.commentId,
                    comment,
                ]),
            )

            for (const comment of post.comments ?? []) {
                const version = {
                    ...comment,
                    versionCreatedTime: entity.createdTime,
                    apiDataId: entity.id,
                }

                if (!commentsMap.has(comment.commentId)) {
                    commentsMap.set(comment.commentId, {
                        ...comment,
                        versions: [version],
                    })
                    continue
                }

                const existingComment = commentsMap.get(comment.commentId)

                if (!existingComment) {
                    continue
                }

                const alreadyExists = existingComment.versions?.some(
                    (v: any) =>
                        v.commentId === comment.commentId &&
                        v.createdTime === comment.createdTime &&
                        v.commentText === comment.commentText,
                )

                if (!alreadyExists) {
                    existingComment.versions = existingComment.versions ?? []
                    existingComment.versions.push(version)
                }
            }

            existingPost.comments = Array.from(commentsMap.values())
            existingPost.commentsCount = existingPost.comments.length
        }
    }

    return Array.from(postsMap.values())
}

const parseDate = (value: string) => {
    const [date, time] = value.split(' ')
    const [day, month, year] = date.split('.').map(Number)
    const [hour, minute, second] = time.split(':').map(Number)

    return new Date(year, month - 1, day, hour, minute, second).getTime()
}

const renderComment = (comment: any, level = 0) => (
    <Box
        key={comment.commentId}
        sx={{
            ml: level * 4,
            mt: 2,
            pl: 2,
            borderLeft: level ? '2px solid #e0e0e0' : undefined,
        }}
    >
        <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar
                src={comment.profilePicture}
                alt={comment.authorName}
                onClick={() => window.open(comment.authorUrl, "_blank", "noopener,noreferrer")}
                sx={{
                    width: 40,
                    height: 40,
                    cursor: "pointer",
                    transition: "0.2s",
                    "&:hover": {
                        transform: "scale(1.08)",
                        boxShadow: 2,
                    },
                }}
            >
                {comment.authorName?.charAt(0)}
            </Avatar>

            <Box flex={1}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography fontWeight={600}>
                        {comment.authorName}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                        {comment.createdTime}
                    </Typography>
                </Stack>

                <Typography
                    sx={{
                        whiteSpace: 'pre-wrap',
                        mt: 0.5,
                    }}
                >
                    {comment.commentText}
                </Typography>
                {comment.versions?.length > 1 && (
                    <Stack spacing={1} mt={1.5}>
                        {comment.versions.map((version: any, index: number) => (
                            <Chip
                                key={`${version.commentId}-${version.apiDataId}-${index}`}
                                size="small"
                                color={index === 0 ? 'primary' : 'default'}
                                label={`v${index + 1} | commentId: ${version.commentId} | ${version.createdTime}`}
                                sx={{ justifyContent: 'flex-start' }}
                            />
                        ))}
                    </Stack>
                )}
                {comment.attachments?.length > 0 && (
                    <Stack
                        direction="row"
                        spacing={1}
                        mt={1.5}
                        mb={1}
                        flexWrap="wrap"
                    >
                        {comment.attachments.map((attachment: any) => (
                            <Avatar
                                key={attachment.id}
                                src={attachment.url}
                                variant="rounded"
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 2,
                                    cursor: "pointer",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    "&:hover": {
                                        transform: "scale(1.05)",
                                        transition: "0.2s",
                                    },
                                }}
                                onClick={() => window.open(attachment.url, "_blank")}
                            />
                        ))}
                    </Stack>
                )}
                <Stack
                    direction="row"
                    spacing={1}
                    mt={1}
                    alignItems="center"
                >
                    <Chip
                        color="primary"
                        size="small"
                        icon={<FavoriteBorderIcon />}
                        label={comment.likesCount}
                    />

                    {comment.commentUrl && (
                        <Link
                            href={comment.commentUrl}
                            target="_blank"
                            underline="hover"
                        >
                            Open
                        </Link>
                    )}
                </Stack>

                {comment.replies?.map((reply: any) =>
                    renderComment(reply, level + 1),
                )}
            </Box>
        </Stack>
    </Box>
)

export const PostsComments = ({ apiData }: Props) => {
    const posts = buildPostsWithCommentVersions(apiData).sort(
    (a: any, b: any) => parseDate(b.createdTime) - parseDate(a.createdTime),
)

    return (
        <Stack spacing={2}>
            {posts.map((post: any) => (
                <Paper
                    key={post.postId}
                    elevation={3}
                    sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 3,
                        transition: 'all .2s',
                        '&:hover': {
                            boxShadow: 8,
                        },
                    }}
                >
                    <Accordion
                        disableGutters
                        defaultExpanded
                        elevation={0}
                        sx={{
                            boxShadow: 'none',
                            '&:before': {
                                display: 'none',
                            },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                py: 2,
                                px: 3,
                                bgcolor: 'grey.50',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '& .MuiAccordionSummary-content': {
                                    alignItems: 'flex-start',
                                },
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="flex-start"
                                width="100%"
                            >
                                <Box
                                    sx={{
                                        width: 90,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Avatar
                                        src={post.profilePicture}
                                        alt={post.authorName}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            window.open(post.authorUrl, '_blank', 'noopener,noreferrer')
                                        }}
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            cursor: 'pointer',
                                            transition: '0.2s',
                                            '&:hover': {
                                                transform: 'scale(1.08)',
                                                boxShadow: 3,
                                            },
                                        }}
                                    >
                                        {post.authorName?.charAt(0)}
                                    </Avatar>

                                    <Typography
                                        variant="caption"
                                        textAlign="center"
                                        sx={{
                                            mt: 1,
                                            wordBreak: 'break-word',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {post.authorName}
                                    </Typography>
                                </Box>

                                <Box flex={1}>
                                    <Typography
                                        fontWeight={700}
                                        sx={{ whiteSpace: 'pre-wrap' }}
                                    >
                                        {post.postText}
                                    </Typography>

                                    {post.attachments?.length > 0 && (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            mt={2}
                                            mb={1}
                                            flexWrap="wrap"
                                        >
                                            {post.attachments.map((attachment: any) => (
                                                <Avatar
                                                    key={attachment.id}
                                                    src={attachment.url}
                                                    variant="rounded"
                                                    sx={{
                                                        width: 72,
                                                        height: 72,
                                                        borderRadius: 2,
                                                        cursor: 'pointer',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        '&:hover': {
                                                            transform: 'scale(1.05)',
                                                            transition: '0.2s',
                                                        },
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        window.open(attachment.url, '_blank')
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    )}

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        mt={1}
                                        alignItems="center"
                                    >
                                        <Chip
                                            color="primary"
                                            size="small"
                                            icon={<FavoriteBorderIcon />}
                                            label={post.likesCount}
                                        />

                                        <Chip
                                            color="primary"
                                            size="small"
                                            icon={<ChatBubbleOutlineIcon />}
                                            label={post.commentsCount}
                                        />

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {post.createdTime}
                                        </Typography>
                                    </Stack>
                                </Box>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <IconButton
                                        color="primary"
                                        size="small"
                                        title="Open post"
                                        onClick={() => window.open(post.postUrl, '_blank', 'noopener,noreferrer')}
                                    >
                                        <OpenInNewIcon fontSize="small" />
                                    </IconButton>

                                    <IconButton
                                        color="primary"
                                        size="small"
                                        title="Author profile"
                                        onClick={() => window.open(post.authorUrl, '_blank', 'noopener,noreferrer')}
                                    >
                                        <PersonIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </AccordionSummary>

                        <AccordionDetails
                            sx={{
                                py: 3,          // сверху и снизу
                                px: 3,
                            }}
                        >

                            {post.comments?.length ? (
                                post.comments.map((comment: any) =>
                                    renderComment(comment),
                                )
                            ) : (
                                <Typography
                                    color="text.secondary"
                                    mt={2}
                                >
                                    No comments
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Paper>
            ))}
        </Stack>
    )
}